import { useQueryClient } from '@tanstack/react-query'
import {
  CalendarClock,
  Paperclip,
  Pencil,
  PenLine,
  Sparkles,
  Trash2,
  UserRound,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import type { MedicalHistoryContent, NoteWithStaff, SoapContent } from '@snootsvet/shared'
import {
  formatName,
  isSummarisableFile,
  LAB_TYPE_LABELS,
  NOTE_TYPE_LABELS,
} from '@snootsvet/shared'

import { FileDownloadLink } from '@/frontend/components/file-download-link'
import { FormattedDateTime } from '@/frontend/components/formatted-datetime'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/frontend/components/ui/alert-dialog'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { ExpandableContent } from '@/frontend/components/ui/expandable-content'
import { RichTextContent } from '@/frontend/components/ui/rich-text-content'
import { Spinner } from '@/frontend/components/ui/spinner'
import { NoteForm } from '@/frontend/features/notes/note.form'
import { useDeleteNote } from '@/frontend/hooks/api/notes/use-delete-note'
import { cn } from '@/frontend/lib/utils'

/** Renders SOAP fields as labelled rows, skipping empty ones. */
function SoapFields({ content }: { content: SoapContent }) {
  const fields = [
    { key: 'subjective', label: 'S' },
    { key: 'objective', label: 'O' },
    { key: 'assessment', label: 'A' },
    { key: 'plan', label: 'P' },
  ] as const

  return (
    <dl className="divide-y divide-border/60">
      {fields.map(({ key, label }) => {
        const value = content[key]
        if (!value) return null
        return (
          <div key={key} className="flex gap-2 py-2 first:pt-0 last:pb-0 text-sm">
            <dt className="w-5 shrink-0 font-semibold text-muted-foreground">{label}</dt>
            <dd>
              <RichTextContent html={value} />
            </dd>
          </div>
        )
      })}
    </dl>
  )
}

/** How often to refresh while a history summary is being generated, and for how long before giving up. */
const SUMMARY_POLL_MS = 5_000
const SUMMARY_POLL_LIMIT_MS = 3 * 60_000

/**
 * The AI summary of a medical history note's attached records. Pending until the background job writes either
 * the summary or an error, so this refreshes the timeline while it waits.
 */
function HistorySummary({
  patientId,
  content,
}: {
  patientId: string
  content: MedicalHistoryContent
}) {
  const queryClient = useQueryClient()
  const pending = !content.summary && !content.summaryError

  useEffect(() => {
    if (!pending) return
    const startedAt = Date.now()
    const interval = setInterval(() => {
      if (Date.now() - startedAt > SUMMARY_POLL_LIMIT_MS) {
        clearInterval(interval)
        return
      }
      queryClient.invalidateQueries({ queryKey: ['patient-timeline', patientId] })
    }, SUMMARY_POLL_MS)
    return () => clearInterval(interval)
  }, [pending, patientId, queryClient])

  return (
    <div className="flex flex-col gap-2 border-t border-ai-border pt-2 text-sm">
      <div className="flex items-center gap-2">
        <Sparkles className="size-3.5 shrink-0 text-ai-fg" />
        <span className="font-medium">AI summary</span>
      </div>
      {pending ? (
        <span className="flex items-center gap-2 text-muted-foreground">
          <Spinner className="size-3.5" />
          Generating summary from the attached records…
        </span>
      ) : content.summaryError ? (
        <span className="text-destructive-fg">{content.summaryError}</span>
      ) : (
        <RichTextContent html={content.summary ?? ''} />
      )}
    </div>
  )
}

/** Renders note content — dispatches to the correct layout based on note type. */
export function NoteContent({ note }: { note: NoteWithStaff }) {
  if (note.type === 'soap') {
    return <SoapFields content={note.content} />
  }
  if (note.type === 'medical_history') {
    const summarised =
      note.content.summary ||
      note.content.summaryError ||
      note.files.some((f) => isSummarisableFile(f.contentType))
    return (
      <div className="flex flex-col gap-2">
        <RichTextContent html={note.content.text} />
        {summarised && <HistorySummary patientId={note.patientId} content={note.content} />}
      </div>
    )
  }
  if (note.type === 'lab_results') {
    return (
      <div className="flex flex-col gap-2 text-sm">
        <div>
          <span className="font-medium">Lab Type: </span>
          {LAB_TYPE_LABELS[note.content.labType]}
        </div>
        {note.content.clinicalNotes && (
          <div>
            <span className="font-medium">Clinical Notes: </span>
            <RichTextContent html={note.content.clinicalNotes} />
          </div>
        )}
        {note.content.clientCommunication && (
          <div>
            <span className="font-medium">Client Communication: </span>
            <RichTextContent html={note.content.clientCommunication} />
          </div>
        )}
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-1">
      <RichTextContent html={note.content.text} />
      {note.type === 'follow_up' && note.content.taskId && (
        <span className="flex items-center gap-1 text-sm text-muted-foreground">
          <CalendarClock className="size-3" />
          Follow-up task created
        </span>
      )}
    </div>
  )
}

const DEFAULT_STYLE = 'bg-card ring-1 ring-foreground/10'
const INTERNAL_STYLE = 'bg-pending/33 ring-1 ring-pending-border/60'

/** Timeline entry for a note not linked to a consultation. */
export function NoteEntry({ note }: { note: NoteWithStaff }) {
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const { mutate: deleteNote, isPending: isDeleting } = useDeleteNote({
    patientId: note.patientId,
    consultationId: note.consultationId ?? undefined,
  })

  const label = NOTE_TYPE_LABELS[note.type] ?? note.type

  const handleDelete = () => {
    deleteNote(note.id, {
      onSuccess: () => {
        toast('Note deleted')
        setDeleteOpen(false)
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to delete note')
      },
    })
  }

  return (
    <>
      <div
        className={cn(
          'overflow-hidden rounded-xl',
          note.type === 'internal' ? INTERNAL_STYLE : DEFAULT_STYLE
        )}
      >
        {/* Header */}
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <span className="text-headline-sm">
              Note: <FormattedDateTime value={note.createdAt} clinicId={null} />
            </span>
            <Badge variant={note.type === 'internal' ? 'pending' : 'outline'} size="sm">
              {label}
            </Badge>
            <div className="ml-auto flex items-center gap-1 -my-1">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setEditOpen(true)}
                aria-label="Edit note"
              >
                <Pencil className="size-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDeleteOpen(true)}
                aria-label="Delete note"
                className="text-destructive-fg"
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          </div>
          <div className="pt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <UserRound className="size-3 shrink-0" />
              {formatName(note.writtenByStaff)}
            </span>
            {/* Credit who actually posted the note when it differs from the author */}
            {note.createdBy !== note.writtenBy && (
              <span className="flex items-center gap-1">
                <PenLine className="size-3 shrink-0" />
                Posted by {formatName(note.createdByStaff)}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="border-t" />
        <div className="px-4 py-2">
          <ExpandableContent>
            <NoteContent note={note} />
          </ExpandableContent>
        </div>
        {note.files.length > 0 && (
          <div className="flex items-center gap-3 border-t px-4 py-3">
            <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
            <ExpandableContent>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
                {note.files.map((file) => (
                  <FileDownloadLink key={file.id} fileId={file.id} fileName={file.fileName} />
                ))}
              </div>
            </ExpandableContent>
          </div>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
          </DialogHeader>
          <NoteForm
            key={note.id}
            patientId={note.patientId}
            noteId={note.id}
            note={note}
            onSuccess={() => setEditOpen(false)}
            onCancel={() => setEditOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete note?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this note and any attached files.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
